import React, { useState, useContext, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { selectLocalPeer, useHMSActions, useHMSStore } from "@100mslive/react-sdk";
import { toast } from "react-toastify";

import RemoteLayout from "./layout";
import { Conference } from "../../components/videoCalling";
import { WorkshopContext } from "../../contexts/workshop.context";
import { Button, CountdownTimer } from "../../components/workshop";
import { defaultRoles } from "../../util/workshop.util";
import { getNextRoute, getPreviousRoute } from "../../util/general.util";
import GroupList from "../../components/workshop/groupList";
import { htmlToText } from "../../util/translations.util";

const TASK_ID = 2;

const Task5 = () => {
  const hmsActions = useHMSActions();
  const currentUser = useHMSStore(selectLocalPeer);

  const [readyByModerator, setReadyByModerator] = useState(false);
  const [allGroups, setAllGroups] = useState([]);
  const [callGroups, setCallGroups] = useState({});
  const [groupTaskStatus, setGroupTaskStatus] = useState({});
  const [currentGroupRole, setCurrentGroupRole] = useState(undefined);
  const [groupId, setGroupId] = useState(undefined);
  const [userGroup, setUserGroup] = useState([]);
  const [startTask, setStartTask] = useState(false);
  const [finishedTask, setFinishedTask] = useState(false);
  const [alerted, setAlerted] = useState(false);
  const [timerStates, setTimerStates] = useState({});

  const {
    socket,
    user: { id, role } = {},
    workshopDetails: { platform } = {},
    connectedUsers,
    disconnectedUsers
  } = useContext(WorkshopContext);

  const { t } = useTranslation();

  const fetchUserGroups = useCallback(() => {
    // Fetch latest user groups whenever connected/disconnected users change
    if (!socket || !id) return;

    socket.emit(
      "userGroupForTask",
      { taskId: TASK_ID, userId: id },
      ({ groupIndex, userGroup, callGroups, currentGroupRole, groupTaskStatus }) => {
        setCurrentGroupRole(currentGroupRole);
        if (groupIndex !== undefined) setGroupId(groupIndex);
        if (userGroup) setUserGroup(userGroup);
        if (callGroups) setCallGroups(callGroups);
        if (groupTaskStatus) {
          setGroupTaskStatus(groupTaskStatus);
          if (groupTaskStatus?.[groupIndex]?.started) setStartTask(true);
          if (groupTaskStatus?.[groupIndex]?.finished) setFinishedTask(true);
        }
      }
    );

    socket.emit("allGroupsOfTask", TASK_ID, (groups) => {
      if (groups) setAllGroups(groups);
    });

    socket.emit("getGroupsWithCallRooms", TASK_ID, (callGroups) => {
      if (callGroups) setCallGroups(callGroups);
    });
  }, [socket, id]);

  useEffect(() => {
    if (groupId === undefined) {
      fetchUserGroups();
    } else {
      // Still fetch allGroups and callGroups on mount/update
      socket.emit("allGroupsOfTask", TASK_ID, (groups) => {
        if (groups) setAllGroups(groups);
      });

      socket.emit("getGroupsWithCallRooms", TASK_ID, (callGroups) => {
        if (callGroups) setCallGroups(callGroups);
      });
    }

    socket.emit("groupTaskStatus", TASK_ID, (status) => {
      if (status) setGroupTaskStatus(status);

      if (groupId === undefined || groupId < 0) return;

      if (status?.[groupId]?.started) setStartTask(true);

      if (status?.[groupId]?.finished) setFinishedTask(true);
    });

    socket.on("groupTaskStatus", (status) => {
      setGroupTaskStatus(status?.[TASK_ID]);

      if (groupId === undefined || groupId < 0) return;

      if (status?.[TASK_ID]?.[groupId]?.started) {
        setStartTask(true);
      }

      // if (status?.[TASK_ID]?.[groupId]?.ready) {
      //   console.log("ready with role", callGroups?.[groupId]);
      //   setReadyByModerator(true);
      //   setCurrentGroupRole(callGroups?.[groupId]);
      // }

      if (status?.[TASK_ID]?.[groupId]?.finished) {
        setFinishedTask(true);
        setCurrentGroupRole(role);
      } else {
        setFinishedTask(false);
      }
    });

    socket.emit("getTaskActivityStatus", TASK_ID, (myTaskStatus) => {
      console.log("myTaskStatus", myTaskStatus);
      if (!myTaskStatus) return;
      setReadyByModerator(myTaskStatus?.ready);
    });

    socket?.on("taskActivityStatus", (taskActivityStatus) => {
      console.log("taskActivityStatus", taskActivityStatus);
      const myTaskStatus = taskActivityStatus[TASK_ID];
      if (!myTaskStatus) return;
      if (myTaskStatus?.ready) {
        setCurrentGroupRole(callGroups?.[groupId]);
        setReadyByModerator(true);
      }
    });

    // Cleanup
    return () => {
      socket.off("groupTaskStatus");
    };
  }, [id, role, groupId, socket]);

  useEffect(() => {
    if (!currentGroupRole || !currentUser) return;

    if (currentUser?.roleName?.toLowerCase() === currentGroupRole?.toLowerCase()) return;
    hmsActions.changeRoleOfPeer(currentUser.id, currentGroupRole?.toLowerCase(), true);
  }, [currentGroupRole, currentUser]);

  // Refetch user groups whenever connectedUsers or disconnectedUsers changes
  useEffect(() => {
    fetchUserGroups();
  }, [connectedUsers, disconnectedUsers, fetchUserGroups]);

  const handleNext = () => {
    if (!readyByModerator) {
      socket.emit("taskActivityStatus", {
        taskId: TASK_ID,
        status: { ready: true }
      });
      setReadyByModerator(true);
    } else {
      if (!allGroupsReady && !alerted) {
        toast.warning(t("task3.notAllGroupsReady"), {
          closeButton: true
        });
        setAlerted(true);
        return;
      }
      const nextRoute = getNextRoute("/task-5", platform);
      socket.emit("navigate", nextRoute);
    }
  };

  const handleComplete = () => {
    updateTaskInfo({
      taskId: TASK_ID,
      groupId,
      started: true,
      finished: true
    });
    setFinishedTask(true);
  };

  const updateTaskInfo = (data) => {
    socket.emit("updateGroupTaskStatus", data);
  };

  const getReadyGroups = () => {
    if (!groupTaskStatus || typeof groupTaskStatus !== "object") {
      return 0;
    }
    return Object.entries(groupTaskStatus).filter(([key, status]) => {
      if (key === "undefined" || !status || Object.keys(status).length === 0) {
        return false;
      }
      return status.started === true && status.finished === true;
    }).length;
  };

  const allGroupsReady = allGroups.length === getReadyGroups();

  const startActivity = () => {
    updateTaskInfo({
      taskId: TASK_ID,
      groupId,
      started: true,
      finished: false,
      ready: true
    });
  };

  const buttonText = !readyByModerator
    ? t("task2.createBreakoutRoom") + " →"
    : role !== defaultRoles.moderator && readyByModerator && !startTask
      ? t("task2.buttonStart")
      : role !== defaultRoles.moderator && !finishedTask && startTask
        ? t("task2.buttonComplete") + " →"
        : finishedTask
          ? t("task2.buttonNext") + " →"
          : startTask
            ? t("task2.buttonComplete") + " →"
            : t("task2.buttonNext") + " →";

  const handleButtonClick = () => {
    if (
      (role === defaultRoles.moderator || role === defaultRoles.moderatorAndParticipant) &&
      !readyByModerator
    ) {
      return handleNext;
    } else if (role !== defaultRoles.moderator && readyByModerator && !startTask) {
      return startActivity;
    } else if (role !== defaultRoles.moderator && !finishedTask && startTask) {
      return handleComplete;
    } else if (
      (role !== defaultRoles.participant && finishedTask) ||
      role === defaultRoles.moderator
    ) {
      return handleNext;
    } else {
      return null;
    }
  };

  const buttonColor = !readyByModerator
    ? "green"
    : role !== defaultRoles.moderator && readyByModerator && !startTask
      ? "orange"
      : role !== defaultRoles.moderator && !finishedTask && startTask
        ? "orange"
        : finishedTask
          ? "green"
          : startTask
            ? "orange"
            : "green";

  const handleBack = () => {
    const previousRoute = getPreviousRoute(`/task-5`, platform);
    socket.emit("navigate", previousRoute);
  };

  const showBackButton =
    role !== defaultRoles.participant && (!readyByModerator || (startTask && finishedTask));

  return (
    <RemoteLayout
      buttonText={buttonText}
      onButtonClick={handleButtonClick()}
      buttonColor={buttonColor}
      showParticipants={true}
      onBackClick={showBackButton && handleBack}>
      <div className="text-center mb-8">
        <h1 className="text-white text-2xl md:text-4xl font-bold flex items-center justify-center gap-2 text-white-100">
          <span className="text-yellow-500">🏆</span>{" "}
          {(role === defaultRoles.moderator && readyByModerator) ||
          (role !== defaultRoles.moderator && finishedTask)
            ? t("task5.waitingRoom.title")
            : role !== defaultRoles.moderator && readyByModerator
              ? t(startTask?'task5.breakoutRoom.titleStarted':'task5.breakoutRoom.title')
              : t("task5.title")}
        </h1>
        <p
          className="mt-4 max-w-2xl text-lg mx-auto text-white-100"
          dangerouslySetInnerHTML={{
            __html:
              (role === defaultRoles.moderator && readyByModerator) ||
              (role !== defaultRoles.moderator && finishedTask)
                ? t("task5.waitingRoom.description")
                : role !== defaultRoles.moderator && readyByModerator
                  ? t(startTask?'task5.breakoutRoom.descriptionStarted':'task5.breakoutRoom.description')
                  : t("task5.description")
          }}
        />
      </div>
      {/* {((!readyByModerator && !finishedTask && !startTask) ||
        (role !== defaultRoles.moderator && readyByModerator && !finishedTask && startTask)) && (
        <div className="bg-orange-50 rounded-lg p-6 max-w-md block md:hidden">
          <h2
            className="text-orange-default font-bold text-xl mb-4"
            dangerouslySetInnerHTML={{ __html: t("task5.listOfGroups.heading") }}
          />

          <ul
            className="list-inside"
            dangerouslySetInnerHTML={{
              __html: t("task5.listOfGroups.group1Description")
            }}
          />
        </div>
      )} */}
      {role !== defaultRoles.moderator && startTask && !finishedTask && (
        <div className="mx-auto mb-12 w-full mt-5">
          <Timers
            groupLength={userGroup.length}
            groupIndex={groupId}
            start={startTask}
            finished={finishedTask}
            role={role}
          />
        </div>
      )}

      <div className={`flex flex-col md:flex-row gap-4 items-center min-w-100vw ${(role !== defaultRoles.moderator && readyByModerator && !finishedTask )&& "md:!flex-col !justify-center" }`}>
        <div className="w-full">
        {(!readyByModerator && !finishedTask && !startTask) &&
             (
            <div className="bg-orange-50 rounded-lg p-6 max-w-md hidden md:block">
              <h2
                className="text-orange-default font-bold text-xl mb-4"
                dangerouslySetInnerHTML={{ __html: t("task5.listOfGroups.heading") }}
              />

              <ul
                className="list-inside"
                dangerouslySetInnerHTML={{
                  __html: t("task5.listOfGroups.group1Description")
                }}
              />
            </div>
          )}
          {((role === defaultRoles.moderator && readyByModerator) ||
            (role !== defaultRoles.moderator && finishedTask)) && (
            <div className="flex flex-col gap-5 max-h-[500px] overflow-y-auto bg-orange-50 rounded-2xl p-4 md:min-w-96 max-w-xl mx-auto">
              <GroupList
                isOnline={true}
                groups={allGroups}
                groupTaskStatus={groupTaskStatus}
                socket={socket}
                taskId={TASK_ID}
              />
            </div>
          )}
        </div>
        <div className={`mx-auto mb-12 w-full mt-5 ${role !== defaultRoles.moderator && readyByModerator && !finishedTask && "flex justify-center items-center"}`}>
          <Conference smallSpace={true} />
        </div>
      </div>
    </RemoteLayout>
  );
};

export default Task5;

const Timers = ({
  start = false,
  finished,
  groupLength,
  groupIndex,
  greyedOnCompletion = false,
  role
}) => {
  const { t } = useTranslation();

  const [timerIndex, setTimerIndex] = useState(0);

  const { socket } = useContext(WorkshopContext);

  const selectTimerTime = (index) => {
    const firstTimer = groupLength === 2 ? 180 : 120;
    const secondTimer = groupLength === 2 ? 90 : 60;
    if (index % 2 === 0) return firstTimer;
    else return secondTimer;
  };

  return (
    <div
      className={`grid grid-cols-2 md:grid-cols-2 lg:grid-cols-${Math.min(groupLength * 2, 4)} gap-2 mx-auto justify-center`}>
      {groupLength > 0 &&
        [...Array(groupLength * 2)].map((_, index) => {
          return (
            <div key={index} className={`flex justify-center items-center flex-col py-2`}>
              <div className="bg-orange-50 rounded-full">
                <CountdownTimer
                  id={`${TASK_ID}_${groupIndex}_${index}`}
                  totalTime={selectTimerTime(index)}
                  start={start && timerIndex === index}
                  finished={timerIndex > index || finished}
                  greyedOnCompletion={greyedOnCompletion}
                  trailColor="#FAE6C8"
                  textColor="#E59536"
                  strokeWidth={5}
                  orangeClock={true}
                  size={100}
                  onComplete={() => {
                    if (index === groupLength * 2 - 1)
                      socket.emit("updateGroupTaskStatus", {
                        taskId: TASK_ID,
                        groupId: groupIndex,
                        started: true,
                        finished: true
                      });
                    setTimerIndex(index + 1);
                  }}
                />
              </div>
              <p
                className={`text-sm text-center mt-2 ${greyedOnCompletion && finished ? "text-[#cccccc]" : "text-white-100"}`}
                dangerouslySetInnerHTML={{
                  __html:
                    index % 2 === 0
                      ? `${htmlToText(t("mtActivityFive.personOne")).split(" ")[0]} ${Math.ceil(index / 2 + 1)}`
                      : t("mtActivityFive.feedback")
                }}
              />
              {/* {role !== defaultRoles.moderator && (
                <Button
                  text={t("buttonComplete")}
                  disabled={timerIndex > index || finished}
                  className={`text-sm sm:text-sm !py-2 !px-3 mt-1 !w-auto !rounded-full`}
                  onClick={() => {
                    socket.emit("updateTime", { id: `${TASK_ID}_${groupIndex}_${index}`, time: 0 });
                    if (timerIndex > index) return;
                    if (index === groupLength * 2 - 1)
                      socket.emit("updateGroupTaskStatus", {
                        taskId: TASK_ID,
                        groupId: groupIndex,
                        started: true,
                        finished: true
                      });
                    setTimerIndex(index + 1);
                  }}
                />
              )} */}
            </div>
          );
        })}
    </div>
  );
};
